// CSV ↔ Exchange-API Reconciliation (börsenUNabhängig).
//
// Hintergrund: Ein Nutzer lädt zuerst CSV-Exports einer Börse hoch (z.B. weil
// API-Sync ein Premium-Feature ist) und verbindet später die API derselben
// Börse. Dann existiert JEDE Buchung doppelt: einmal als `csv_import`, einmal
// als `exchange_api`. Der vorhandene contentHash-Dedup (txFingerprint, Tier 2)
// fängt das NICHT zuverlässig: CSV-Exports tragen oft Lokalzeit OHNE Offset, die
// API liefert UTC → der tsBucket (und damit der Hash) divergiert, im Extremfall
// rollt das Datum sogar auf den Folgetag (z.B. Kauf 23:41 lokal → 30. statt 29.).
//
// Doktrin: Die API ist die genaueste Quelle. Wenn für eine CSV-Zeile ein
// exakter API-Zwilling existiert, GEWINNT die API-Zeile — sie ist kanonisch.
// Die CSV-Zeile weicht, ABER ihre vom Nutzer/Auto-Matcher gesetzten Verbindungen
// und Klassifikationen (Transfer-Pärchen, externalTransfer, Wallet-Anreicherung)
// werden VORHER auf die API-Zeile übertragen, damit kein Transfer-Paar zerbricht
// (vgl. fix_stale_replace_data_loss: Re-Import/Replace darf nie eine gematchte
// Gegenseite verwaisen lassen → Phantombestand).
//
// CSV-Zeilen OHNE API-Pendant (ältere Historie außerhalb des API-Fensters,
// Bitpanda-Netzwerkgebühren die die API gar nicht liefert, …) bleiben UNANGETASTET.
//
// Reine Funktionen, keine Firestore-Abhängigkeit. Der Verbraucher
// (useTransactions.addBatch) entscheidet selbst, welche Seite gelöscht/gepatcht
// wird und hängt die Transfer-Gegenseite um. Mirror-Vorbild: moonpayLedgerDedupe.js.

import { normalizeExchange, tsMillis } from './txFingerprint';

// Quellen, deren Zeilen ein API-Duplikat sein KÖNNEN (automatisch importiert).
// 'manual' bewusst NICHT: handentrierte TX sind Nutzer-Autorität und werden nie
// automatisch gegen eine API-Zeile aufgelöst.
const MERGEABLE_CSV_SOURCES = new Set([
  'csv_import', 'image_import', 'ai_import', 'migrated_from_local',
]);

// Mengen-Toleranz: CSV und API liefern i.d.R. identische Mengen (die Fee-in-Coin-
// Helfer sind Spiegel, vgl. arch_exchange_api_adapters). abs deckt Sub-Satoshi-
// Float-Drift, rel deckt minimale Parser-Rundungen.
const AMOUNT_ABS_TOL = 1e-8;
const AMOUNT_REL_TOL = 1e-4;
// Datums-Fenster: fängt den Lokalzeit→UTC-Tagessprung (max 1 Tag) mit Reserve.
const DATE_WINDOW_DAYS = 2;
const DAY_MS = 86400000;

function isApiRow(tx) {
  return !!tx && (tx.source === 'exchange_api' || !!tx.externalId);
}

function isCsvCandidate(tx) {
  if (!tx || isApiRow(tx)) return false;
  return MERGEABLE_CSV_SOURCES.has(tx.source);
}

function sym(tx)    { return String(tx.coin    || tx.sym    || '').toUpperCase(); }
function symOut(tx) { return String(tx.coinOut || tx.symOut || '').toUpperCase(); }
function symIn(tx)  { return String(tx.coinIn  || tx.symIn  || '').toUpperCase(); }

function amountsMatch(a, b) {
  a = Number(a); b = Number(b);
  if (!(a > 0) || !(b > 0)) return false;
  const diff = Math.abs(a - b);
  if (diff <= AMOUNT_ABS_TOL) return true;
  return diff / Math.max(a, b) <= AMOUNT_REL_TOL;
}

/** Tages-Differenz zweier 'YYYY-MM-DD' (robust gegen Lokalzeit-vs-UTC-Drift,
 *  weil rein datumsbasiert statt zeitstempelbasiert). */
function dayDiff(a, b) {
  if (!a || !b) return Infinity;
  const da = Date.parse(`${a}T00:00:00Z`);
  const db = Date.parse(`${b}T00:00:00Z`);
  if (Number.isNaN(da) || Number.isNaN(db)) return Infinity;
  return Math.abs(Math.round((da - db) / DAY_MS));
}

function sameExchange(a, b) {
  const ea = normalizeExchange(a.exchange || a.platform);
  const eb = normalizeExchange(b.exchange || b.platform);
  return !!ea && ea === eb;
}

/** Inhaltliche Gleichheit ohne Zeitstempel: Börse + Typ + Coin(s) + Menge(n). */
function coreMatch(csv, api) {
  if ((csv.typ || '') !== (api.typ || '')) return false;
  if (!sameExchange(csv, api)) return false;
  if (csv.typ === 'swap') {
    return symOut(csv) === symOut(api)
      && symIn(csv) === symIn(api)
      && amountsMatch(csv.mengeOut, api.mengeOut)
      && amountsMatch(csv.mengeIn, api.mengeIn);
  }
  return sym(csv) === sym(api) && amountsMatch(csv.menge, api.menge);
}

/** Deterministischer Sort-Vergleich (Invariant 7: Rebuild reproduzierbar). */
function cmpStable(a, b) {
  return String(a.datum || '').localeCompare(String(b.datum || ''))
    || String(a.zeit || '').localeCompare(String(b.zeit || ''))
    || sym(a).localeCompare(sym(b))
    || (Number(a.menge || 0) - Number(b.menge || 0))
    || String(a.externalId || a.id || a._docId || '')
        .localeCompare(String(b.externalId || b.id || b._docId || ''));
}

/**
 * Findet alle CSV↔API-Zwillinge in einer kombinierten TX-Liste (existing + neu).
 * Reihenfolge-unabhängig: deckt CSV→API, API→CSV, beide-neu und bestehende
 * Altlast (existing↔existing) ab. 1:1-greedy — ein API-Eintrag bindet höchstens
 * einen CSV-Eintrag; bei mehreren Kandidaten gewinnt der zeitnächste (z.B. zwei
 * gleich große Käufe am selben Tag → korrekt 1:1 statt kollabiert).
 *
 * @param {Array} allTxs  alle bekannten TX (Original-Referenzen)
 * @returns {Array<{csv: object, api: object}>}  Paare; `api` ist kanonisch.
 */
export function findCsvApiTwins(allTxs) {
  if (!Array.isArray(allTxs) || allTxs.length === 0) return [];
  const apis = allTxs.filter(isApiRow);
  if (apis.length === 0) return [];
  const csvs = allTxs.filter(isCsvCandidate);
  if (csvs.length === 0) return [];

  const apisSorted = [...apis].sort(cmpStable);
  const usedCsv = new Set();
  const pairs = [];

  for (const api of apisSorted) {
    let best = null;
    let bestDelta = Infinity;
    for (const csv of csvs) {
      if (usedCsv.has(csv)) continue;
      if (dayDiff(csv.datum, api.datum) > DATE_WINDOW_DAYS) continue;
      if (!coreMatch(csv, api)) continue;
      const delta = Math.abs(tsMillis(csv) - tsMillis(api));
      if (delta < bestDelta || (delta === bestDelta && best && cmpStable(csv, best) < 0)) {
        best = csv;
        bestDelta = delta;
      }
    }
    if (best) {
      usedCsv.add(best);
      pairs.push({ csv: best, api });
    }
  }
  return pairs;
}

/**
 * Felder, die von der weichenden CSV-Zeile auf die kanonische API-Zeile übertragen
 * werden. Regel: Verbindungen/Anreicherung nur FÜLLEN, was die API-Zeile nicht
 * selbst hat; Nutzer-Klassifikation (externalTransfer/sourceMode/userEdited) wird
 * übernommen, wenn die API-Zeile dazu nichts sagt.
 *
 * Wichtig: `linkedTxId` zeigt auf die _docId der Gegenseite und bleibt gültig —
 * die Gegenseite muss separat vom CSV-_docId auf das API-_docId umgehängt werden
 * (macht der Verbraucher, hier nicht möglich da keine Firestore-Kenntnis).
 *
 * @returns {object}  Patch (nur tatsächlich zu ändernde Felder; leer = nichts).
 */
export function mergeFieldsFromCsv(api, csv) {
  const patch = {};
  if (!api.linkedTxId && csv.linkedTxId) patch.linkedTxId = csv.linkedTxId;
  if (!api.transferGroupId && csv.transferGroupId) patch.transferGroupId = csv.transferGroupId;
  if (!api.manualLink && csv.manualLink) patch.manualLink = csv.manualLink;
  if (csv.externalTransfer != null && api.externalTransfer == null) patch.externalTransfer = csv.externalTransfer;
  if (csv.sourceMode && !api.sourceMode) patch.sourceMode = csv.sourceMode;
  if (csv.userEdited && !api.userEdited) patch.userEdited = true;
  if (!api.wallet && csv.wallet) patch.wallet = csv.wallet;
  if (!api.walletHash && csv.walletHash) patch.walletHash = csv.walletHash;
  if (csv.notiz && csv.notiz !== 'CSV Import' && !api.notiz) patch.notiz = csv.notiz;
  return patch;
}
